import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Badge,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Label as LabelIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  AutoAwesome as AutoIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import imageService from '../api/imageService';
import AuthenticatedImage from './AuthenticatedImage';
import { getServerUrl } from '../config/runtimeConfig';

const SceneTaggingDialogImproved = ({ 
  open, 
  onClose, 
  sessionId, 
  sessionName,
  images = [], // Available images for selection
  onSceneTaggingComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sceneData, setSceneData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  
  // Selection state
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // Real-time analysis state
  const [analysisProgress, setAnalysisProgress] = useState({
    status: 'idle', // idle, downloading, analyzing, completed, failed
    current: 0,
    total: 0,
    currentImage: null,
    message: ''
  });
  const [analysisResults, setAnalysisResults] = useState([]);
  const [socket, setSocket] = useState(null);
  const [analysisJobId, setAnalysisJobId] = useState(null);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (open && sessionId) {
      const newSocket = io(getServerUrl(), {
        path: '/socket.io/',
        withCredentials: true
      });

      newSocket.on('connect', () => {
        console.log('🔌 Socket.IO connected:', newSocket.id);
        // Join the analysis room for this session
        newSocket.emit('joinAnalysisRoom', { sessionId });
      });

      newSocket.on('sceneAnalysisProgress', (data) => {
        console.log('📈 FRONTEND: Scene Analysis Progress Update');
        console.log('📊 Progress Details:', {
          jobId: data.jobId,
          sessionId: data.sessionId,
          status: data.status,
          progress: data.progress,
          total: data.total,
          currentImage: data.currentImage,
          message: data.message,
          timestamp: data.timestamp
        });
        
        if (data.jobId === analysisJobId || data.sessionId === sessionId) {
          console.log('✅ Progress update matches current analysis - updating UI');
          setAnalysisProgress({
            status: data.status,
            current: data.progress,
            total: data.total,
            currentImage: data.currentImage,
            message: data.message
          });
        } else {
          console.log('⚠️ Progress update doesn\'t match current analysis - ignoring');
        }
      });

      newSocket.on('sceneAnalysisResult', (data) => {
        console.log('🎯 FRONTEND: Individual Scene Analysis Result');
        console.log('📋 Result Details:', {
          jobId: data.jobId,
          sessionId: data.sessionId,
          imageResult: {
            id: data.imageResult.id,
            name: data.imageResult.name,
            success: data.imageResult.success,
            sceneType: data.imageResult.sceneData?.sceneType,
            tags: data.imageResult.sceneData?.tags,
            locationSite: data.imageResult.sceneData?.locationSite,
            tokensUsed: data.imageResult.metadata?.tokensUsed
          },
          progress: data.progress,
          total: data.total,
          timestamp: data.timestamp
        });
        
        if (data.jobId === analysisJobId || data.sessionId === sessionId) {
          console.log('✅ Result matches current analysis - adding to results');
          setAnalysisResults(prev => {
            const newResults = [...prev, data.imageResult];
            console.log('📊 Updated results array:', {
              totalResults: newResults.length,
              latestResult: data.imageResult.name
            });
            return newResults;
          });
        } else {
          console.log('⚠️ Result doesn\'t match current analysis - ignoring');
        }
      });

      newSocket.on('sceneAnalysisComplete', (data) => {
        console.log('🎉 FRONTEND: Scene Analysis Complete!');
        console.log('📊 Completion Summary:', {
          jobId: data.jobId,
          sessionId: data.sessionId,
          status: data.status,
          summary: data.summary,
          totalResults: data.results?.length,
          totalErrors: data.errors?.length,
          sceneGroupCount: data.summary?.sceneGroups?.length,
          totalTokensUsed: data.summary?.totalTokensUsed,
          timestamp: data.timestamp
        });
        
        if (data.jobId === analysisJobId || data.sessionId === sessionId) {
          console.log('✅ Completion matches current analysis - finalizing UI');
          setAnalyzing(false);
          setAnalysisProgress(prev => ({ 
            ...prev, 
            status: 'completed',
            message: 'Analysis completed successfully!'
          }));
          
          console.log('🔄 Loading updated scene data...');
          // Load updated scene data
          loadSceneData();
          
          if (onSceneTaggingComplete) {
            console.log('📤 Calling completion callback');
            onSceneTaggingComplete(data);
          }
        } else {
          console.log('⚠️ Completion doesn\'t match current analysis - ignoring');
        }
      });

      newSocket.on('sceneAnalysisError', (data) => {
        console.error('❌ FRONTEND: Scene Analysis Error');
        console.error('💥 Error Details:', {
          jobId: data.jobId,
          sessionId: data.sessionId,
          imageId: data.imageId,
          imageName: data.imageName,
          error: data.error,
          progress: data.progress,
          total: data.total,
          timestamp: data.timestamp
        });
        
        if (data.jobId === analysisJobId || data.sessionId === sessionId) {
          console.error('🚨 Error matches current analysis - showing to user');
          setError(`Error analyzing ${data.imageName}: ${data.error}`);
        } else {
          console.log('⚠️ Error doesn\'t match current analysis - ignoring');
        }
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Socket.IO disconnected');
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [open, sessionId, analysisJobId]);

  // Load existing scene data when dialog opens
  useEffect(() => {
    if (open && sessionId) {
      loadSceneData();
      // Initialize selection with all images
      if (images.length > 0) {
        setSelectedImages(new Set(images.map(img => img.uniqueId)));
        setSelectAll(true);
      }
    }
  }, [open, sessionId, images]);

  const loadSceneData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Loading scene data for session:', sessionId);
      const response = await imageService.getSessionScenes(sessionId);
      console.log('📊 Scene data response:', response);
      
      setSceneData(response);
      
      // Auto-expand first few groups
      if (response.sceneGroups?.length > 0) {
        setExpandedGroups(new Set([
          response.sceneGroups[0].groupId,
          ...(response.sceneGroups[1] ? [response.sceneGroups[1].groupId] : [])
        ]));
      }
    } catch (error) {
      console.error('❌ Failed to load scene data:', error);
      setError('Failed to load existing scene data');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelection = (imageId) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
    setSelectAll(newSelected.size === images.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedImages(new Set());
      setSelectAll(false);
    } else {
      setSelectedImages(new Set(images.map(img => img.uniqueId)));
      setSelectAll(true);
    }
  };

  const analyzeSelectedScenes = async () => {
    console.log('🎯 FRONTEND: Starting Scene Analysis');
    
    try {
      if (!sessionId) {
        console.error('❌ No session ID available');
        setError('No session ID available for analysis');
        return;
      }

      if (selectedImages.size === 0) {
        console.error('❌ No images selected');
        setError('Please select at least one image to analyze');
        return;
      }

      console.log('✅ Pre-flight checks passed');
      console.log('📋 Analysis Request Details:', {
        sessionId,
        selectedCount: selectedImages.size,
        selectedIds: Array.from(selectedImages),
        timestamp: new Date().toISOString()
      });

      setAnalyzing(true);
      setError(null);
      setAnalysisProgress({
        status: 'starting',
        current: 0,
        total: selectedImages.size,
        currentImage: null,
        message: 'Starting analysis...'
      });
      setAnalysisResults([]);
      
      console.log('🚀 Calling backend API for scene analysis...');
      const response = await imageService.analyzeSessionScenes(
        sessionId, 
        Array.from(selectedImages)
      );
      
      console.log('📊 FRONTEND: Backend API response received');
      console.log('✅ API Response Details:', {
        success: response.success,
        analysisJobId: response.analysisJobId,
        sessionId: response.sessionId,
        status: response.status,
        totalImages: response.totalImages,
        selectedImages: response.selectedImages,
        message: response.message,
        timestamp: response.timestamp
      });
      
      setAnalysisJobId(response.analysisJobId);
      console.log('🔄 Analysis job started, waiting for WebSocket updates...');
      
    } catch (error) {
      console.error('💥 FRONTEND: Scene analysis error');
      console.error('❌ Error Details:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      setError(`Failed to start analysis: ${error.message}`);
      setAnalyzing(false);
    }
  };

  const stopAnalysis = () => {
    setAnalyzing(false);
    setAnalysisProgress(prev => ({ ...prev, status: 'stopped' }));
    // Note: In a full implementation, you'd send a stop signal to the backend
  };

  const toggleGroupExpansion = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const getSceneTypeIcon = (sceneType) => {
    const lowerType = sceneType?.toLowerCase() || '';
    if (lowerType.includes('indoor') || lowerType.includes('social')) return '🏠';
    if (lowerType.includes('outdoor') || lowerType.includes('nature')) return '🌳';
    if (lowerType.includes('event') || lowerType.includes('celebration')) return '🎉';
    if (lowerType.includes('travel') || lowerType.includes('tourism')) return '✈️';
    if (lowerType.includes('food') || lowerType.includes('dining')) return '🍽️';
    if (lowerType.includes('sports') || lowerType.includes('activity')) return '⚽';
    if (lowerType.includes('work') || lowerType.includes('professional')) return '💼';
    return '📸';
  };

  const renderImageSelection = () => (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Select Images to Analyze
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              indeterminate={selectedImages.size > 0 && selectedImages.size < images.length}
              onChange={handleSelectAll}
            />
          }
          label={`Select All (${images.length})`}
        />
      </Box>
      
      <Typography variant="body2" color="text.secondary" mb={2}>
        {selectedImages.size} of {images.length} images selected
      </Typography>

      <Grid container spacing={1} sx={{ maxHeight: 200, overflow: 'auto' }}>
        {images.slice(0, 20).map((image) => (
          <Grid item xs={3} sm={2} md={1.5} key={image.uniqueId}>
            <Card 
              sx={{ 
                position: 'relative',
                cursor: 'pointer',
                border: selectedImages.has(image.uniqueId) ? 2 : 0,
                borderColor: 'primary.main'
              }}
              onClick={() => handleImageSelection(image.uniqueId)}
            >
              <AuthenticatedImage
                imageId={image.uniqueId}
                alt={image.name}
                endpoint="thumbnail"
                style={{
                  width: '100%',
                  height: 60,
                  objectFit: 'cover'
                }}
              />
              <Box
                position="absolute"
                top={4}
                right={4}
              >
                {selectedImages.has(image.uniqueId) ? (
                  <CheckBoxIcon color="primary" fontSize="small" />
                ) : (
                  <CheckBoxOutlineBlankIcon color="action" fontSize="small" />
                )}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {images.length > 20 && (
        <Typography variant="caption" color="text.secondary" mt={1}>
          Showing first 20 images. {images.length - 20} more available.
        </Typography>
      )}
    </Paper>
  );

  const renderAnalysisProgress = () => {
    if (!analyzing && analysisProgress.status === 'idle') return null;

    return (
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            Analysis Progress
          </Typography>
          {analyzing && (
            <Button
              startIcon={<StopIcon />}
              onClick={stopAnalysis}
              color="error"
              size="small"
            >
              Stop
            </Button>
          )}
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={(analysisProgress.current / analysisProgress.total) * 100}
          sx={{ mb: 1 }}
        />
        
        <Typography variant="body2" color="text.secondary">
          {analysisProgress.message}
        </Typography>
        
        <Typography variant="caption" color="text.secondary">
          {analysisProgress.current} of {analysisProgress.total} images
        </Typography>

        {analysisProgress.currentImage && (
          <Box mt={1}>
            <Typography variant="caption">
              Current: {analysisProgress.currentImage.name}
            </Typography>
          </Box>
        )}

        {analysisResults.length > 0 && (
          <Box mt={2}>
            <Typography variant="body2" gutterBottom>
              Recent Results ({analysisResults.length}):
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {analysisResults.slice(-5).map((result, index) => (
                <Chip 
                  key={index}
                  label={result.sceneData?.sceneType || 'Unknown'}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const renderSceneGroups = () => {
    if (!sceneData || (!sceneData.sceneGroups?.length && !sceneData.ungroupedImages?.length)) {
      return (
        <Alert severity="info">
          No scene analysis data available. Start an analysis to see results.
        </Alert>
      );
    }

    return (
      <Box>
        {sceneData.sceneGroups?.map((group) => (
          <Accordion
            key={group.groupId}
            expanded={expandedGroups.has(group.groupId)}
            onChange={() => toggleGroupExpansion(group.groupId)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                <Typography variant="h6">
                  {getSceneTypeIcon(group.groupName)} {group.groupName}
                </Typography>
                <Badge badgeContent={group.images?.length || 0} color="primary">
                  <PhotoLibraryIcon />
                </Badge>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {group.images?.slice(0, 12).map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.uniqueId}>
                    <Card>
                      <AuthenticatedImage
                        imageId={image.uniqueId}
                        alt={image.name}
                        endpoint="thumbnail"
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover'
                        }}
                      />
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="caption" noWrap>
                          {image.name}
                        </Typography>
                        {image.sceneAnalysis?.locationSite && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            📍 {image.sceneAnalysis.locationSite}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {group.images?.length > 12 && (
                <Typography variant="caption" color="text.secondary" mt={1}>
                  Showing 12 of {group.images.length} images
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        ))}

        {sceneData.ungroupedImages?.length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                📂 Ungrouped Images ({sceneData.ungroupedImages.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {sceneData.ungroupedImages.slice(0, 12).map((image) => (
                  <Grid item xs={6} sm={4} md={3} key={image.uniqueId}>
                    <Card>
                      <AuthenticatedImage
                        imageId={image.uniqueId}
                        alt={image.name}
                        endpoint="thumbnail"
                        style={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover'
                        }}
                      />
                      <CardContent sx={{ p: 1 }}>
                        <Typography variant="caption" noWrap>
                          {image.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoIcon color="primary" />
          <Typography variant="h6">
            Scene Analysis & Organization
          </Typography>
          {sessionName && (
            <Chip label={sessionName} size="small" variant="outlined" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Image Selection */}
            {images.length > 0 && renderImageSelection()}

            {/* Analysis Progress */}
            {renderAnalysisProgress()}

            {/* Scene Groups */}
            {renderSceneGroups()}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
        
        <Button 
          onClick={loadSceneData}
          startIcon={<RefreshIcon />}
          disabled={loading}
        >
          Refresh
        </Button>

        <Button
          onClick={analyzeSelectedScenes}
          startIcon={analyzing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          variant="contained"
          disabled={analyzing || selectedImages.size === 0}
        >
          {analyzing ? 'Analyzing...' : `Analyze Selected (${selectedImages.size})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SceneTaggingDialogImproved;
